/**
 * ไอคอนสกิล/บัฟ/ดีบัฟเป็นภาพ (รอบ 20) — สัญลักษณ์ SVG ที่ออกแบบเอง ~20 แบบ บนพื้นไล่สีของสกิล
 *
 * `SkillDef.glyph` มี ~150 ค่า → `symbolFor` จับคู่ด้วยคำในชื่อ (slash → ดาบ, frost → เกล็ดหิมะ ...) ไม่ต้องวาดทีละตัว
 * **วางภาพจริงแทนได้**: `client/src/assets/icons/skills/<id สกิล>.png` (หรือ .webp) — ระบบหยิบไฟล์นั้นก่อนเสมอ
 * (import.meta.glob ตอน build แบบเดียวกับ model-files.ts จึงไม่ยิงขอไฟล์ที่ไม่มี)
 * pure (ยกเว้น glob) · ผลเป็น data URI ใช้ใน CSS `background-image` · แคชต่อ key
 */

import { ICON_FILES as FILES } from "./icon-files.js";

export function iconFileFor(id: string): string | undefined {
  for (const [path, url] of Object.entries(FILES)) {
    const base = path.split("/").pop()!.replace(/\.(png|webp)$/, "");
    if (base === id) return url;
  }
  return undefined;
}

/* ---------- สัญลักษณ์ (viewBox 0 0 24 24 · สีขาว) ---------- */

const SYM: Record<string, string> = {
  blade: '<path d="M18 3l3 0 0 3-9.5 9.5-3-3z"/><path d="M6.5 13.5l4 4-1.5 1.5-1.5-1.5-2.5 2.5-1.5-1.5 2.5-2.5-1.5-1.5z"/>',
  hammer: '<path d="M4 6h10v5H4z"/><path d="M8 11h2.5v10H8z"/><path d="M16 4l4 2-3 3"/>',
  shield: '<path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5z"/>',
  roar: '<path d="M4 9h3l5-4v14l-5-4H4z"/><path d="M15 8c1.5 1 1.5 7 0 8M18 6c3 2 3 10 0 12" fill="none" stroke="#fff" stroke-width="1.8"/>',
  fist: '<path d="M6 8h11a2 2 0 012 2v5a6 6 0 01-6 6h-3a4 4 0 01-4-4z"/><path d="M6 8V5h3v3M9.5 8V4h3v4M13 8V5h3v3" />',
  flame: '<path d="M12 2c1 4 6 6 6 12a6 6 0 01-12 0c0-3 2-5 3-6 0 2 1 3 2 3 0-4-1-6 1-9z"/>',
  snow: '<path d="M11 2h2v20h-2z"/><path d="M11 2h2v20h-2z" transform="rotate(60 12 12)"/><path d="M11 2h2v20h-2z" transform="rotate(-60 12 12)"/>',
  bolt: '<path d="M14 2L5 14h6l-2 8 9-12h-6z"/>',
  swirl: '<path d="M12 4a8 8 0 018 8h-3a5 5 0 00-5-5 3 3 0 00-3 3 2 2 0 002 2v3a5 5 0 01-5-5 6 6 0 016-6z"/><path d="M5 16h14v2H5zM8 19.5h8V21H8z"/>',
  wave: '<path d="M2 14c3-4 5-4 8 0s5 4 8 0 3-3 4-2v4c-1-1-2-1-4 1-3 4-5 4-8 0s-5-4-8 0z"/><path d="M2 8c3-3 5-3 8 0s5 3 8 0l4-1v2c-2 1-2 2-4 3-3 2-5 1-8-1s-5-2-8 0z"/>',
  rock: '<path d="M4 18l3-8 4-3 5 2 4 6-2 4H6z"/>',
  arrow: '<path d="M3 21l13-13" stroke="#fff" stroke-width="2.2"/><path d="M14 4l7-1-1 7z"/><path d="M3 17l2 2-2 2-1-3zM6 20l-2 1 1-3z"/>',
  trap: '<circle cx="12" cy="12" r="8" fill="none" stroke="#fff" stroke-width="2"/><path d="M12 4v16M4 12h16M6.5 6.5l11 11M17.5 6.5l-11 11" stroke="#fff" stroke-width="1.4"/>',
  eye: '<path d="M2 12c4-6 16-6 20 0-4 6-16 6-20 0z"/><circle cx="12" cy="12" r="3.3" fill="#1b1b2a"/>',
  drop: '<path d="M12 2c3 5 7 8 7 13a7 7 0 01-14 0c0-5 4-8 7-13z"/><circle cx="9.5" cy="15" r="1.6" fill="#1b1b2a"/><circle cx="14" cy="13" r="1.1" fill="#1b1b2a"/>',
  moon: '<path d="M15 3a9 9 0 100 18 7 7 0 010-18z"/>',
  heart: '<path d="M12 21C6 16 3 13 3 9a4.5 4.5 0 019-1 4.5 4.5 0 019 1c0 4-3 7-9 12z"/>',
  sun: '<circle cx="12" cy="12" r="4.5"/><path d="M11 1h2v4h-2zM11 19h2v4h-2zM1 11h4v2H1zM19 11h4v2h-4zM4 5.4L5.4 4l2.8 2.8-1.4 1.4zM15.8 17.2l1.4-1.4 2.8 2.8-1.4 1.4zM4 18.6l2.8-2.8 1.4 1.4L5.4 20zM15.8 6.8L18.6 4 20 5.4l-2.8 2.8z"/>',
  star: '<path d="M12 2l2.9 6.6 7.1.6-5.4 4.7 1.6 7.1L12 17.3 5.8 21l1.6-7.1L2 9.2l7.1-.6z"/>',
  rage: '<path d="M12 3a8 8 0 00-8 8c0 3 2 5 3 6v4h10v-4c1-1 3-3 3-6a8 8 0 00-8-8z"/><path d="M7 10l4 2M17 10l-4 2" stroke="#1b1b2a" stroke-width="1.8"/><path d="M9 16h6" stroke="#1b1b2a" stroke-width="1.6"/>',
  gem: '<path d="M7 3h10l4 6-9 12L3 9z"/><path d="M3 9h18M12 21L8 9l4-6 4 6z" fill="none" stroke="#1b1b2a" stroke-width="1" opacity=".5"/>',
  lantern: '<path d="M9 2h6v2H9z"/><path d="M7 5h10l-1 13H8z"/><path d="M8 19h8v3H8z"/><path d="M12 8c1 2 2 3 2 5a2 2 0 01-4 0c0-2 1-3 2-5z" fill="#ffd77a"/>',
  skull: '<path d="M12 2a8 8 0 00-8 8c0 3 1.5 5 3 6v4h10v-4c1.5-1 3-3 3-6a8 8 0 00-8-8z"/><circle cx="9" cy="11" r="2" fill="#1b1b2a"/><circle cx="15" cy="11" r="2" fill="#1b1b2a"/>',
  slow: '<circle cx="12" cy="13" r="8" fill="none" stroke="#fff" stroke-width="2"/><path d="M11 8h2v6h-2zM11 13h5v2h-5zM9 2h6v2H9z"/>',
  chain: '<path d="M7 9a4 4 0 015.6 0l1.4 1.4-1.4 1.4-1.4-1.4a2 2 0 00-2.8 2.8l2.8 2.8a2 2 0 002.8 0l1.4 1.4a4 4 0 01-5.6 0L7 14.6A4 4 0 017 9z"/><path d="M17 15a4 4 0 01-5.6 0L10 13.6l1.4-1.4 1.4 1.4a2 2 0 002.8-2.8L12.8 8a2 2 0 00-2.8 0L8.6 6.6a4 4 0 015.6 0L17 9.4A4 4 0 0117 15z"/>',
  brokenShield: '<path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5z"/><path d="M13 4l-3 6 3 2-2 7" fill="none" stroke="#1b1b2a" stroke-width="1.8"/>',
};

/** คำในชื่อ glyph/id → สัญลักษณ์ (ตรวจตามลำดับ ตัวแรกที่เจอชนะ) */
const RULES: Array<[RegExp, string]> = [
  [/meteor|flame|fire|inferno|flare|ember|burn|sunburst/, "flame"],
  [/frost|ice|blizzard|avalanche|snow|freeze/, "snow"],
  [/spark|lightning|bolt|thunder|storm|shock/, "bolt"],
  [/tide|tidal|water|maelstrom|wave|hydro|aqua/, "wave"],
  [/wind|gust|cyclone|tempest|whirlwind|breath/, "swirl"],
  [/stone|earth|quake|rock|ground/, "rock"],
  [/shield|aegis|fortress|barrier|ward|guard|stance|parry|unbroken|cloak/, "shield"],
  [/roar|cry|call|taunt|shout/, "roar"],
  [/fist|combo|uppercut|flurry|rush|rampage|thousand|crusher|knuckle|punch/, "fist"],
  [/bash|slam|smash|hammer|heartstop|finisher/, "hammer"],
  [/arrow|shot|snipe|shower|heartshot|barrage|volley|rapid|double|pierce|pin/, "arrow"],
  [/trap|net|mine|prison|snare/, "trap"],
  [/eye|eagle|focus|patience|aim/, "eye"],
  [/venom|poison|bleed|toxin/, "drop"],
  [/blind|nightfall|mirage|flicker|shadow|dusk|night/, "moon"],
  [/mend|heal|renew|miracle|massres|soul|calm|bless|sanctuary|regen/, "heart"],
  [/glow|ray|beam|sunlance|judgement|radiance|dawn|holy|purge|sun/, "sun"],
  [/star|comet|starfall|ascend|arcane|sigil|grit/, "star"],
  [/fury|bloodlust|frenzy|berserk|rage|fervor/, "rage"],
  [/slash|blade|cross|edge|fang|whirl|cleave|sweep|twin|fan|dagger|sword|cut/, "blade"],
  [/lantern|wick/, "lantern"],
  [/passive/, "gem"],
];

export function symbolFor(key: string): string {
  const k = key.toLowerCase();
  for (const [re, sym] of RULES) if (re.test(k)) return sym;
  return "star";
}

const cache = new Map<string, string>();

function svgUri(sym: string, c0: string, c1: string, ring?: string): string {
  const key = `${sym}|${c0}|${c1}|${ring ?? ""}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">`
    + `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c0}"/><stop offset="1" stop-color="${c1}"/></linearGradient>`
    + `<radialGradient id="h" cx=".35" cy=".25" r=".8"><stop offset="0" stop-color="#fff" stop-opacity=".35"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient></defs>`
    + `<rect x="1" y="1" width="30" height="30" rx="7" fill="url(#g)"/>`
    + `<rect x="1" y="1" width="30" height="30" rx="7" fill="url(#h)"/>`
    + `<g transform="translate(4 4)" fill="#fff" stroke="none" style="filter:drop-shadow(0 1px 1px rgba(0,0,0,.45))">${SYM[sym] ?? SYM["star"]}</g>`
    + `<rect x="1" y="1" width="30" height="30" rx="7" fill="none" stroke="${ring ?? "rgba(0,0,0,.35)"}" stroke-width="${ring ? 2.5 : 1.2}"/>`
    + `</svg>`;
  // ใช้ ' ไม่ใช่ " — ผลลัพธ์ถูกใส่ใน style="..." ของ HTML (encodeURIComponent ไม่แปลง ' แต่ SVG ไม่มี ' อยู่แล้ว)
  const uri = `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}')`;
  cache.set(key, uri);
  return uri;
}

/** ไอคอนสกิล (CSS background-image) — ภาพที่วางไว้ก่อน ไม่มีก็สัญลักษณ์ */
export function skillIcon(sk: { id: string; glyph: string; colors: [string, string] }): string {
  const file = iconFileFor(sk.id);
  if (file) return `url('${file}')`;
  return svgUri(symbolFor(`${sk.glyph} ${sk.id}`), sk.colors[0], sk.colors[1]);
}

/** ไอคอนบัฟที่ไม่ใช่สกิล (พรตะเกียง ของกิน ฯลฯ) */
export function buffIcon(id: string): string {
  if (id === "lantern_blessing") return svgUri("lantern", "#ffcf5a", "#a8551a");
  if (id === "adexp") return svgUri("star", "#ffe27a", "#b07a12");
  return svgUri(symbolFor(id), "#4a6583", "#1e2c3d");
}

/** ไอคอนดีบัฟ ขอบแดง */
const DEBUFF_SYM: Record<string, [string, string, string]> = {
  poison: ["drop", "#8a4adf", "#3a1a6a"],
  slow: ["slow", "#4aa8ff", "#163a6a"],
  weaken: ["skull", "#7a5a6a", "#2a1a24"],
  stun: ["star", "#e8c84a", "#6a5212"],
  root: ["chain", "#8a6a3a", "#3a2a12"],
  armorbreak: ["brokenShield", "#9a9aa8", "#3a3a48"],
};
export function debuffIcon(kind: string): string {
  const d = DEBUFF_SYM[kind] ?? ["skull", "#a33", "#411"];
  return svgUri(d[0], d[1], d[2], "#ff4a4a");
}
